import { flexRender } from '@tanstack/react-table'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'

import { cn } from '@/utils/cn'

import type { ExtendedColumnMeta, TableHeaderProps } from '../DataTable.types'
import {
  getAlignmentClass,
  getAriaSort,
  getStickyColumnClass,
  getStickyColumnStyles
} from '../DataTable.utils'

export const TableHeader = <TData,>({
  table,
  stickyHeader = false,
  enableSorting = true,
  enableResizing = false,
  className
}: TableHeaderProps<TData>) => {
  return (
    <thead className={cn(stickyHeader && 'sticky top-0 z-20 bg-background', className)}>
      {table.getHeaderGroups().map((headerGroup) => (
        <tr key={headerGroup.id} className="border-b transition-colors hover:bg-muted/50">
          {headerGroup.headers.map((header) => {
            const meta = header.column.columnDef.meta as ExtendedColumnMeta | undefined
            const canSort = enableSorting && header.column.getCanSort()
            const canResize = enableResizing && header.column.getCanResize()
            const sortDirection = header.column.getIsSorted()

            return (
              <th
                key={header.id}
                className={cn(
                  'h-12 px-4 text-left align-middle font-medium text-muted-foreground',
                  'border-l-0 border-r-0 border-t-0',
                  meta?.headerClassName,
                  getAlignmentClass(meta?.align),
                  getStickyColumnClass(meta?.sticky),
                  canSort && 'cursor-pointer select-none hover:text-foreground',
                  sortDirection && 'text-foreground'
                )}
                style={{
                  width: meta?.width,
                  minWidth: meta?.minWidth,
                  maxWidth: meta?.maxWidth,
                  ...getStickyColumnStyles(meta?.sticky)
                }}
                onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                aria-sort={canSort ? getAriaSort(sortDirection) : undefined}
                aria-label={
                  meta?.ariaLabel ||
                  (typeof header.column.columnDef.header === 'string'
                    ? header.column.columnDef.header
                    : undefined)
                }
              >
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </div>

                  {canSort && (
                    <SortIndicator direction={sortDirection} className="ml-auto flex-shrink-0" />
                  )}

                  {canResize && (
                    <div
                      className={cn(
                        'absolute right-0 top-0 h-full w-1 cursor-col-resize select-none',
                        'bg-transparent hover:bg-border',
                        header.column.getIsResizing() && 'bg-primary'
                      )}
                      onMouseDown={header.getResizeHandler()}
                      onTouchStart={header.getResizeHandler()}
                    />
                  )}
                </div>

                {meta?.description && <div className="sr-only">{meta.description}</div>}
              </th>
            )
          })}
        </tr>
      ))}
    </thead>
  )
}

// Sort indicator component
interface SortIndicatorProps {
  direction: false | 'asc' | 'desc'
  className?: string
}

const SortIndicator: React.FC<SortIndicatorProps> = ({ direction, className }) => {
  const iconProps = {
    className: cn('h-4 w-4 transition-colors', className),
    'aria-hidden': true
  }

  if (direction === 'asc') {
    return <ArrowUp {...iconProps} />
  }

  if (direction === 'desc') {
    return <ArrowDown {...iconProps} />
  }

  return (
    <ArrowUpDown {...iconProps} className={cn(iconProps.className, 'text-muted-foreground/50')} />
  )
}
